// 📄 pages/Proyectos/ProjectSolutionsHome.tsx
// Índice único del proyecto (/proyecto/:projectId), en 3 secciones:
// Favoritos (condicional) → Soluciones activas (el core) → Más del
// proyecto (Herramientas, Mi Espacio — visualmente más discreto).
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Star } from 'lucide-react';
import { useProject } from '../../context/ProjectContext.tsx';
import { SOLUCIONES } from './solucionesConfig.ts';
import { HERRAMIENTAS_ITEMS, IconoDe } from '../Dashboard/menuConfig.tsx';
import ProjectStatsPanel from '../../components/proyecto/ProjectStatsPanel.tsx';
import HomeCard from '../../components/common/HomeCard.tsx';
import { useFavorites } from '../../hooks/useFavorites.ts';
import { FavoriteItemType, FavoriteMetadata } from '../../services/favoriteService.ts';
import AusenciasProximaSemanaBanner from './components/AusenciasProximaSemanaBanner.tsx';
import TareasProximasVencerBanner from './components/TareasProximasVencerBanner.tsx';

interface FavoriteCardData {
  icon: React.ReactNode;
  label: string;
  tagline: string;
  onClick: () => void;
}

export default function ProjectSolutionsHome() {
  const { project, projectId, esCliente } = useProject();
  const navigate = useNavigate();
  const { favorites, isFavorite, toggleFavorite } = useFavorites(projectId);

  if (!project) return null;

  // Proyectos creados antes de solucionesActivas: se tratan como solo
  // "commerce", igual que en cualquier otro lugar que lea este campo.
  const activas = project.solucionesActivas || ['commerce'];

  const herramientaCard = (): FavoriteCardData => ({
    icon: '🛠️',
    label: 'Herramientas',
    tagline: 'Validador de Estructura, Pruebas APIs y Configuración del proyecto.',
    onClick: () => navigate(`/proyecto/${projectId}/herramientas`)
  });

  const miEspacioCard = (): FavoriteCardData => ({
    icon: '🔖',
    label: 'Mi Espacio',
    tagline: 'Snippets, notas de SAP, pendientes y contactos privados para este proyecto.',
    onClick: () => navigate(`/proyecto/${projectId}/mi-espacio`)
  });

  // Reconstruye la card de un favorito a partir de su (itemType, itemId) —
  // no se guarda nada más que eso en Firestore. Si el item ya no existe
  // (solución desactivada del proyecto, herramienta renombrada/quitada) el
  // favorito queda huérfano: se salta en silencio (sin cleanup automático
  // del doc, ver Fase 0) y se deja un warning para debug.
  const resolveFavoriteCard = (fav: FavoriteMetadata): FavoriteCardData | null => {
    if (fav.itemType === 'solution') {
      const meta = SOLUCIONES[fav.itemId];
      if (!meta || !activas.includes(fav.itemId)) {
        console.warn(`Favorito huérfano: solución "${fav.itemId}" ya no existe o no está activa en este proyecto.`);
        return null;
      }
      return { icon: meta.icon, label: meta.label, tagline: meta.tagline, onClick: () => navigate(`/proyecto/${projectId}/${meta.path}`) };
    }

    if (fav.itemType === 'tool') {
      if (fav.itemId === 'herramientas') return herramientaCard();

      const item = HERRAMIENTAS_ITEMS.find((h) => h.id === fav.itemId);
      if (!item || (item.soloConsultor && esCliente)) {
        console.warn(`Favorito huérfano: herramienta "${fav.itemId}" ya no existe o no está disponible para este usuario.`);
        return null;
      }
      return {
        icon: <IconoDe icon={item.icon} size={32} />,
        label: item.label,
        tagline: item.tagline || '',
        onClick: () => navigate(`/proyecto/${projectId}/${item.id}`)
      };
    }

    if (fav.itemType === 'space' && fav.itemId === 'mi-espacio') return miEspacioCard();

    console.warn(`Favorito huérfano: item desconocido (${fav.itemType}/${fav.itemId}).`);
    return null;
  };

  const favoriteCards = favorites
    .map((fav) => ({ fav, card: resolveFavoriteCard(fav) }))
    .filter((x): x is { fav: FavoriteMetadata; card: FavoriteCardData } => x.card !== null);

  const favStar = (itemType: FavoriteItemType, itemId: string) => ({
    isFavorite: isFavorite(itemType, itemId),
    onToggleFavorite: () => toggleFavorite(itemType, itemId)
  });

  return (
    <div className="space-y-8">
      <AusenciasProximaSemanaBanner project={project} projectId={projectId} />
      <TareasProximasVencerBanner projectId={projectId} />
      <ProjectStatsPanel projectId={projectId} erpTipo={project.erpTipo} solucionesActivas={activas} />

      {/* ── Favoritos (solo si hay al menos uno) ──────────────────────── */}
      {favoriteCards.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" /> Favoritos
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {favoriteCards.map(({ fav, card }) => (
              <HomeCard
                key={`${fav.itemType}_${fav.itemId}`}
                icon={card.icon}
                label={card.label}
                tagline={card.tagline}
                onClick={card.onClick}
                isFavorite
                onToggleFavorite={() => toggleFavorite(fav.itemType, fav.itemId)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Soluciones activas (el core de la vista) ──────────────────── */}
      <div className="space-y-3">
        <p className="text-gray-400">Soluciones activas en este proyecto:</p>

        {activas.length === 0 && (
          <div className="text-center py-12 bg-white/5 border border-white/10 rounded-2xl text-gray-500">
            Este proyecto no tiene ninguna solución activa todavía. Editalo para habilitar al menos una.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {activas.map((slug) => {
            const meta = SOLUCIONES[slug];
            if (!meta) return null;
            return (
              <HomeCard
                key={slug}
                icon={meta.icon}
                label={meta.label}
                tagline={meta.tagline}
                onClick={() => navigate(`/proyecto/${projectId}/${meta.path}`)}
                {...favStar('solution', slug)}
              />
            );
          })}
        </div>
      </div>

      {/* ── Más del proyecto (discreto: Herramientas, Mi Espacio) ─────── */}
      <div className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Más del proyecto</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <HomeCard {...herramientaCard()} {...favStar('tool', 'herramientas')} />
          <HomeCard {...miEspacioCard()} {...favStar('space', 'mi-espacio')} />
        </div>
      </div>
    </div>
  );
}
